import React, { useState, useEffect } from 'react';
import styles from '../Dashboards.mod.less';

import SectionedList from '../../common/SectionedList/SectionedList';
import Spinner from '../../common/Spinner';

import ReportListEmptyState from './ReportListEmptyState';

import {
  CONTEXT_DEFAULT_VIEW_SETTINGS,
  reportTypeToIconClass,
  reportTypeToRecordType,
  reportTypeToSearchPlaceholder,
} from '../helpers';

/**
 * @typedef ReportListProps
 * @prop {*=} selectedReportType
 * @prop {*=} selectedReport
 * @prop {*=} setSelectedReport
 * @prop {*=} bookmarkId
 * @prop {*=} context
 * @prop {*=} defaultSelectedReportType
 * @prop {*=} additionalFetchParams
 * @prop {*=} emptyStateHeader
 * @prop {*=} emptyStateBody
 */
/**
 * @type {React.FC<ReportListProps>}
 */
function ReportList({
  selectedReportType,
  selectedReport,
  setSelectedReport,
  bookmarkId,
  context,
  defaultSelectedReportType = null,
  additionalFetchParams = {},
  emptyStateHeader = null,
  emptyStateBody = null,
}) {
  const [reports, setReports] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [loadingReports, setLoadingReports] = useState(true);

  let recordType;
  // If we select All or Favorites from the sidebar, then selectedReportType will be 'all'
  // or 'favorites' but for the default view we always want it to be just the passed in report type
  if (context === CONTEXT_DEFAULT_VIEW_SETTINGS) {
    recordType = reportTypeToRecordType(defaultSelectedReportType, context);
  } else {
    recordType = reportTypeToRecordType(selectedReportType, context);
  }

  // TODO: This should probably get moved into a shared hook for either searching or api calls
  // Add a useReducer to handle the state changes, similar to here: https://github.com/aha-app/netlify-flexsearch/blob/master/index.js#L26-L58
  useEffect(() => {
    setLoadingReports(true);
    let searchParams = {
      report_type: 'reports',
      record_type: recordType,
      dashboard_folder_id: 'any',
      search_term: searchString,
      per_page: 200,
      dashboard_id: bookmarkId,
    };

    if (selectedReportType === 'recents') searchParams['recent'] = 'true';
    if (selectedReportType === 'favorites') searchParams['favorite'] = 'true';
    if (selectedReportType === 'created_by_me')
      searchParams['created_by'] = window.currentUser.id;

    let searchUrl = `/bookmarks.json?${new URLSearchParams({
      ...searchParams,
      ...additionalFetchParams,
    }).toString()}`;

    fetch(searchUrl)
      .then(response => response.json())
      .then(response => {
        setReports(response.reports);
        setLoadingReports(false);
      })
      .catch(error => console.error('Error loading reports: ' + error));
  }, [selectedReportType, searchString, recordType, bookmarkId]); // eslint-disable-line react-hooks/exhaustive-deps

  let listContents;

  if (loadingReports) {
    listContents = <Spinner />;
  } else if (!reports || reports.length === 0) {
    listContents = (
      <ReportListEmptyState
        selectedReportType={selectedReportType}
        searchString={searchString}
        context={context}
        emptyStateHeader={emptyStateHeader}
        emptyStateBody={emptyStateBody}
      />
    );
  } else if (selectedReportType && reports) {
    const options = reports.map(report => {
      const iconClass = reportTypeToIconClass(report.record_type);
      const nameDiv = (
        <div className={styles.ReportListReportName}>
          {iconClass && <i className={iconClass} />}
          {report.name}
        </div>
      );
      return {
        text: nameDiv,
        value: report.id,
        report: report,
      };
    });
    if (options.length > 199) {
      options.push({
        text: 'Too many results. Search to see more…',
      });
    }

    listContents = (
      <SectionedList
        options={[{ key: 'report-list', children: options }]}
        onClick={option => setSelectedReport(option.report)}
        selected={selectedReport && selectedReport.id}
      />
    );
  }

  return (
    <div className={styles.ReportListMain}>
      <div className={styles.SearchBox}>
        <aha-search-field
          placeholder={`Search all ${reportTypeToSearchPlaceholder(
            selectedReportType
          )}`}
          value={searchString}
          style={{ width: '100%' }}
          onInput={event => {
            setSearchString(event.target.value);
          }}
        />
      </div>
      {listContents}
    </div>
  );
}

export default ReportList;
